import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import * as AccountItemApi from 'web-app/react/entities/account-items/api';
import * as Api from 'web-app/react/entities/bill-payers/api';
import * as InvoiceApi from 'web-app/api/invoice-api';
import {showDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as ChildActions from 'web-app/react/entities/children/actions';

import * as Actions from './actions';

const search = action$ =>
    action$
        .ofType(Actions.search.type)
        .debounceTime(500)
        .mergeMap(({payload}) => {
            if (payload.term) {
                return Api.searchBillPayer(payload.institutionId, null, payload.term)
                    .flatMap(({billPayers, children}) => [
                        ChildActions.fetchAllSuccess.action(children),
                        Actions.searchSuccess.action({billPayers}),
                    ])
                    .catch(e => Observable.of(Actions.searchFailed.action(e)));
            } else {
                return Observable.of(Actions.searchAborted.action());
            }
        });

const moveInvoice = action$ =>
    action$
        .ofType(Actions.moveInvoice.type)
        .mergeMap(({payload}) =>
            showDialogObservable({
                title: i18next.t('accountItems.move.transferConfirmTitle'),
                description: i18next.t('accountItems.move.transferConfirmDescription', {
                    name: payload.newBillPayerName,
                }),
                okText: i18next.t('accountItems.move.transferConfirm'),
            }).map(selection => ({payload, selection})),
        )
        .mergeMap(({payload, selection}) => {
            if (selection !== DialogChoice.OK) {
                return Observable.of(Actions.moveInvoiceAborted.action());
            } else {
                return Observable.fromPromise(
                    InvoiceApi.moveInvoice(
                        payload.institutionId,
                        payload.billPayerId,
                        payload.invoiceId,
                        payload.newBillPayerId,
                    ),
                )
                    .map(reply => {
                        /*
                            The API for moving invoices responds with something of the form:
                            {
                                invoice: {
                                    [invoiceId]: {
                                        id: String,
                                        ...other properties
                                    }
                                }
                            }

                            However, it always just returns a single invoice. Being a bit defensive with this though.
                        */
                        const invoiceId = Object.keys(reply.invoice)[0];
                        const invoice = reply.invoice[invoiceId];

                        if (!invoice) {
                            return Actions.moveInvoiceAborted.action();
                        }

                        return Actions.moveInvoiceSuccess.action({invoice, oldBillPayerId: payload.billPayerId});
                    })
                    .catch(e => Observable.of(Actions.moveInvoiceFailed.action(e)));
            }
        });

const movePayment = action$ =>
    action$
        .ofType(Actions.movePayment.type)
        .mergeMap(({payload}) =>
            showDialogObservable({
                description: i18next.t('accountItems.move.transferPaymentConfirmDescription', {
                    name: payload.newBillPayerName,
                }),
                title: i18next.t('accountItems.move.transferPaymentConfirmTitle'),
                okText: i18next.t('accountItems.move.transferConfirm'),
            }).map(selection => ({payload, selection})),
        )
        .mergeMap(({payload, selection}) => {
            if (selection !== DialogChoice.OK) {
                return Observable.of(Actions.movePaymentAborted.action());
            } else {
                return AccountItemApi.movePayment(
                    payload.institutionId,
                    payload.billPayerId,
                    payload.paymentId,
                    payload.newBillPayerId,
                )
                    .map(reply =>
                        Actions.movePaymentSuccess.action({...reply.payment, oldBillPayerId: payload.billPayerId}),
                    )
                    .catch(e => Observable.of(Actions.movePaymentFailed.action(e)));
            }
        });

export default combineEpics(search, moveInvoice, movePayment);
